import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function WholesaleGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-wholesale';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AllChinaBuySpreadsheet Wholesale Guide: Bulk Orders Made Simple',
    description: 'Complete wholesale buying guide using AllChinaBuySpreadsheet. Learn which categories offer the best bulk margins, understand MOQ and tiered pricing, and master freight forwarding for large China product orders.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Wholesale Guide', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Large%20warehouse%20with%20bulk%20packaged%20products%20on%20pallets%2C%20industrial%20setting%2C%20bright%20overhead%20lighting%2C%20wholesale%20distribution%20concept%2C%20professional%20logistics%20photography%2C%20organized%20inventory%2C%20business%20scale%20operations%2C%20efficient%20supply%20chain&width=1800&height=840&seq=wholesale-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet wholesale guide"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Wholesale</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Wholesale Guide: Bulk Orders Made Simple</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Buying in bulk from China can unlock massive savings — but only if you know which suppliers offer genuine
            wholesale pricing and which ones are just repackaging retail inventory. The
            <strong className="text-primary-600 font-semibold"> AllChinaBuySpreadsheet</strong> wholesale system
            identifies true bulk-friendly listings with tiered pricing that actually rewards larger orders.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Identifying Wholesale-Friendly Products</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Not every product category is suitable for wholesale purchasing. Using the
            <strong className="text-primary-600 font-semibold"> all china buy spreadsheet</strong> data, we have
            identified that Shoes, T-Shirts, Hoodies, and Accessories consistently offer the best wholesale margins.
            These categories have mature supply chains with suppliers accustomed to bulk orders, meaning you get
            genuine volume discounts rather than minor percentage reductions.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            The <strong className="text-primary-600 font-semibold">china product spreadsheet list</strong> for wholesale
            includes minimum order quantities (MOQ), tiered pricing breakdowns, and estimated per-unit costs at each
            volume level. This transparency is something traditional wholesale platforms rarely provide upfront.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Bulk Shipping and Logistics</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            When ordering in bulk, shipping strategy changes dramatically. The
            <strong className="text-primary-600 font-semibold"> spreadsheet shopping guide China</strong> includes
            freight forwarder recommendations for large orders, estimated sea freight timelines versus air freight,
            and customs clearance guidance specific to commercial quantities. Our data shows that buyers using
            spreadsheet-optimized bulk ordering save an average of 40-55% compared to piecemeal purchasing.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Start Bulk Buying Today</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Browse Wholesale <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-pricing" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Pricing Strategy: Best Deals Guide &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-shipping" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Shipping Guide: Delivery &amp; Tracking &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}